import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query
} from 'firebase/firestore';
import { auth, db, ADMIN_EMAIL } from '../../firebase/config';
import {
  FaArrowLeft,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSpinner,
  FaSave,
  FaTimes,
  FaHistory,
  FaGripVertical,
  FaGlobe,
  FaRocket,
  FaUsers,
  FaLightbulb,
  FaAward,
  FaGlobeAmericas,
  FaCog,
  FaCubes,
  FaWrench,
  FaStar,
  FaSpaceShuttle
} from 'react-icons/fa';
import './AdminTimeline.css';

const ICON_OPTIONS = [
  { value: 'rocket', label: 'Rocket', icon: <FaRocket /> },
  { value: 'users', label: 'Users', icon: <FaUsers /> },
  { value: 'lightbulb', label: 'Lightbulb', icon: <FaLightbulb /> },
  { value: 'award', label: 'Award', icon: <FaAward /> },
  { value: 'globe', label: 'Globe', icon: <FaGlobeAmericas /> },
  { value: 'cog', label: 'Settings', icon: <FaCog /> },
  { value: 'cubes', label: 'Cubes', icon: <FaCubes /> },
  { value: 'wrench', label: 'Wrench', icon: <FaWrench /> },
  { value: 'star', label: 'Star', icon: <FaStar /> },
  { value: 'shuttle', label: 'Shuttle', icon: <FaSpaceShuttle /> }
];

const getIconComponent = (iconValue) => {
  const iconOption = ICON_OPTIONS.find(opt => opt.value === iconValue);
  return iconOption ? iconOption.icon : <FaRocket />;
};

const AdminTimeline = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [timelineList, setTimelineList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState('en');
  const [formData, setFormData] = useState({
    year: '',
    title_en: '',
    description_en: '',
    title_fr: '',
    description_fr: '',
    icon: 'rocket',
    order: 0
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser && currentUser.email.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
        setUser(currentUser);
        fetchTimeline();
      } else {
        navigate('/admin');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchTimeline = async () => {
    try {
      const q = query(collection(db, 'timeline'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTimelineList(items);
    } catch (err) {
      console.error('Error fetching timeline:', err);
      if (err.code === 'permission-denied' || err.message?.includes('permission')) {
        setError('Firestore permissions not configured. Please update Firestore rules to include "timeline" collection.');
      } else {
        setError('Failed to load timeline items');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      year: '',
      title_en: '',
      description_en: '',
      title_fr: '',
      description_fr: '',
      icon: 'rocket',
      order: timelineList.length
    });
    setEditingId(null);
    setShowForm(false);
    setActiveTab('en');
    setError('');
  };

  const handleEdit = (item) => {
    setFormData({
      year: item.year || '',
      title_en: item.title_en || item.title || '',
      description_en: item.description_en || item.description || '',
      title_fr: item.title_fr || '',
      description_fr: item.description_fr || '',
      icon: item.icon || 'rocket',
      order: item.order || 0
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.year.trim() || !formData.title_en.trim()) {
      setError('Year and English title are required.');
      setActiveTab('en');
      return;
    }

    setSaving(true);

    try {
      const timelineData = {
        year: formData.year.trim(),
        title_en: formData.title_en.trim(),
        description_en: formData.description_en.trim(),
        title_fr: formData.title_fr.trim(),
        description_fr: formData.description_fr.trim(),
        // Legacy fields for backward compatibility
        title: formData.title_en.trim(),
        description: formData.description_en.trim(),
        icon: formData.icon,
        order: formData.order,
        updatedAt: new Date().toISOString()
      };

      if (editingId) {
        await updateDoc(doc(db, 'timeline', editingId), timelineData);
        setSuccess('Timeline item updated successfully!');
      } else {
        timelineData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'timeline'), timelineData);
        setSuccess('Timeline item added successfully!');
      }

      await fetchTimeline();
      resetForm();
    } catch (err) {
      console.error('Error saving timeline:', err);
      setError(err.message || 'Failed to save timeline item. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.year} - ${item.title_en || item.title}"?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'timeline', item.id));
      setSuccess('Timeline item deleted successfully!');
      await fetchTimeline();
    } catch (err) {
      console.error('Error deleting timeline:', err);
      setError('Failed to delete timeline item');
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <FaSpinner className="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="admin-timeline">
      <header className="admin-timeline-header">
        <button onClick={() => navigate('/admin/dashboard')} className="back-btn">
          <FaArrowLeft /> Back to Dashboard
        </button>
        <h1><FaHistory /> Notre Parcours (Timeline)</h1>
      </header>

      <main className="admin-timeline-content">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {!showForm ? (
          <>
            <div className="timeline-actions">
              <button onClick={() => setShowForm(true)} className="add-btn">
                <FaPlus /> Add New Timeline Event
              </button>
            </div>

            <div className="admin-timeline-list">
              {timelineList.length === 0 ? (
                <div className="empty-state">
                  <FaHistory />
                  <h3>No timeline events yet</h3>
                  <p>Click "Add New Timeline Event" to create your first item.</p>
                </div>
              ) : (
                timelineList.map((item, index) => (
                  <div key={item.id} className="admin-timeline-item">
                    <div className="admin-timeline-item-order">
                      <FaGripVertical />
                      <span>{index + 1}</span>
                    </div>
                    <div className="admin-timeline-item-icon">
                      {getIconComponent(item.icon)}
                    </div>
                    <div className="admin-timeline-item-year">
                      {item.year}
                    </div>
                    <div className="admin-timeline-item-content">
                      <div className="admin-timeline-item-langs">
                        <span className="lang-badge en">EN: {item.title_en || item.title || 'Not set'}</span>
                        <span className="lang-badge fr">FR: {item.title_fr || 'Not set'}</span>
                      </div>
                      <p>{item.description_en || item.description}</p>
                    </div>
                    <div className="admin-timeline-item-actions">
                      <button onClick={() => handleEdit(item)} className="edit-btn">
                        <FaEdit /> Edit
                      </button>
                      <button onClick={() => handleDelete(item)} className="delete-btn">
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="timeline-form-container">
            <h2>{editingId ? 'Edit Timeline Event' : 'Add New Timeline Event'}</h2>

            {/* Language Tabs */}
            <div className="language-tabs">
              <button
                type="button"
                className={`lang-tab ${activeTab === 'en' ? 'active' : ''}`}
                onClick={() => setActiveTab('en')}
              >
                <FaGlobe /> English
              </button>
              <button
                type="button"
                className={`lang-tab ${activeTab === 'fr' ? 'active' : ''}`}
                onClick={() => setActiveTab('fr')}
              >
                <FaGlobe /> French
              </button>
            </div>

            <form onSubmit={handleSubmit} className="timeline-form">
              {/* English Content */}
              <div className={`lang-content ${activeTab === 'en' ? 'active' : ''}`}>
                <div className="form-group">
                  <label>Title (English) *</label>
                  <input
                    type="text"
                    value={formData.title_en}
                    onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                    placeholder="e.g., Company Founded"
                  />
                </div>

                <div className="form-group">
                  <label>Description (English)</label>
                  <textarea
                    value={formData.description_en}
                    onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                    placeholder="Describe this milestone in English..."
                    rows={3}
                  />
                </div>
              </div>

              {/* French Content */}
              <div className={`lang-content ${activeTab === 'fr' ? 'active' : ''}`}>
                <div className="form-group">
                  <label>Title (French)</label>
                  <input
                    type="text"
                    value={formData.title_fr}
                    onChange={(e) => setFormData({ ...formData, title_fr: e.target.value })}
                    placeholder="e.g., Fondation de l'entreprise"
                  />
                </div>

                <div className="form-group">
                  <label>Description (French)</label>
                  <textarea
                    value={formData.description_fr}
                    onChange={(e) => setFormData({ ...formData, description_fr: e.target.value })}
                    placeholder="Décrivez cette étape en français..."
                    rows={3}
                  />
                </div>
              </div>

              {/* Common Fields (always visible) */}
              <div className="common-fields">
                <div className="form-row">
                  <div className="form-group">
                    <label>Year *</label>
                    <input
                      type="text"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                      placeholder="e.g., 2018"
                    />
                  </div>

                  <div className="form-group">
                    <label>Display Order</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Icon</label>
                  <div className="icon-selector">
                    {ICON_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`icon-option ${formData.icon === option.value ? 'selected' : ''}`}
                        onClick={() => setFormData({ ...formData, icon: option.value })}
                        title={option.label}
                      >
                        {option.icon}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={resetForm} className="cancel-btn">
                  <FaTimes /> Cancel
                </button>
                <button type="submit" disabled={saving} className="save-btn">
                  {saving ? (
                    <><FaSpinner className="spinner" /> Saving...</>
                  ) : (
                    <><FaSave /> {editingId ? 'Update' : 'Save'} Event</>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminTimeline;
